import { IdContato, IdGrupo, IdInbox } from "../common-types";
import { ShortContato } from "../contatos/contatos";
import { GenericServiceBase } from "../generic-service";
import { MessageContent, WhizMessageTipo } from "../mensagens/mensagens.service";
import { usePermissoesStore } from "@/stores/app/permissoesStore";
import { useAuthStoreUser } from "@/stores/app/authUser";
import { ResponseUsuario } from "../usuarios/usuarios.service";

export type IsoTimestamp = string

/** `sempre` = até o usuário reativar. */
export type DuracaoSilencio = '8h' | '1semana' | 'sempre'

export type InfoMensagemGrupo = {
    tipo: WhizMessageTipo,
    contato: ShortContato,
    conteudo: MessageContent
    enviadaPorMim: boolean
}

export type ResponseGrupo = {
    id             : IdGrupo,
    idInbox        : IdInbox,

    nome: string,
    descricao?: string|null,
    imagem?: string|null,

    codigo: string,

    ultimaMensagemData: IsoTimestamp|null,
    ultimaMensagemInfo: InfoMensagemGrupo|null,

    participantes?: null,
    naoLidas?: number|null,
    marcadoNaoLido?: boolean,
    isPinned?: boolean,
    /** Silenciado pelo usuário logado: sem som nem push das mensagens. */
    silenciado?: boolean,
    /** Fim do silêncio; `null` = até reativar. */
    silenciadoAte?: IsoTimestamp|null,

    /**
     * Comunidade à qual o grupo pertence, quando pertence. Vem resolvida do servidor
     * (id + nome + foto): a linha da comunidade é filtrada da listagem, então não há
     * como o front buscá-la por conta própria.
     */
    comunidade?: { id: string; nome: string; imagem: string | null } | null,

    /** Grupo em modo announce: apenas administradores enviam. */
    somenteAdmins?: boolean,

    whitelistEnabled?: boolean,
    whitelist?: ResponseUsuario[],
    etiquetas?: { id: string; nome: string; cor: string; descricao?: string | null; del: boolean }[],
}

export type ComunidadeSubgrupo = {
    id: string;
    /** Sempre presente: `Grupo.nome` é coluna não-nula. */
    nome: string;
    imagem: string | null;
    ehGrupoAvisos: boolean;
    somenteAdmins: boolean;
    /** É o grupo de onde o modal foi aberto. */
    ehGrupoAtual: boolean;
}

export type ComunidadeDetalhe = {
    id: string;
    nome: string;
    descricao: string | null;
    imagem: string | null;
    idInbox: IdInbox;
    subgrupos: ComunidadeSubgrupo[];
}

export type FetchGroupsWrapper = {
    dados: ResponseGrupo[], total: number
}

export class GrupoService extends GenericServiceBase {
    constructor() { super('grupos') }

    /**
     * 🔥 NOVO: Filtra apenas as inboxes onde o usuário tem permissão de ver grupos
     */
    private filtrarInboxesComPermissao(idsInboxes?: string[]): string[] {
        const permissoesStore = usePermissoesStore();
        const authStore = useAuthStoreUser();
        
        const idConta = authStore.currentConta?.id;
        if (!idConta) {
            console.warn('[GrupoService] Sem conta ativa, retornando array vazio');
            return [];
        }

        const inboxesDisponiveis = permissoesStore.minhasPermissoes[idConta]?.inboxes || {};
        const todosIdsInboxes = Object.keys(inboxesDisponiveis);

        // Se não passou inboxes específicas, usa todas disponíveis
        const inboxesParaVerificar = idsInboxes && idsInboxes.length > 0 
            ? idsInboxes 
            : todosIdsInboxes;

        // Filtra apenas as que têm permissão
        const inboxesComPermissao = inboxesParaVerificar.filter(idInbox => 
            permissoesStore.temPermissao('inbox.ver_grupos', idConta, idInbox)
        );

        // console.log('[GrupoService] Filtro de permissões:');
        // console.log('  Inboxes solicitadas:', inboxesParaVerificar);
        // console.log('  Inboxes com permissão:', inboxesComPermissao);

        return inboxesComPermissao;
    }

    async getByUsuario(pageSize: number = 10, page: number = 1, idsInboxes?: string[]) {
        // 🔥 FILTRA INBOXES COM PERMISSÃO
        const idsInboxesComPermissao = this.filtrarInboxesComPermissao(idsInboxes);

        // console.log("AKKKKAKAKAKAKAKKAKAKAKAKKAKAKAKA",idsInboxes)
        
        // Se não tem permissão em nenhuma inbox, retorna vazio
        if (idsInboxesComPermissao.length === 0) {
            console.log('[GrupoService] Sem permissão em nenhuma inbox, retornando vazio');
            return { dados: [], total: 0 } as FetchGroupsWrapper;
        }

        const urlParams = new URLSearchParams();
        urlParams.append('resultadosPorPagina', String(pageSize));
        urlParams.append('pagina', String(page));
        
        // Adiciona apenas as inboxes com permissão
        idsInboxesComPermissao.forEach(id => {
            urlParams.append('idsInboxes', id);
        });

        const grupos = await this.f.get('por-usuario', undefined, {queryParams: urlParams}) as FetchGroupsWrapper
        grupos.dados.forEach(g => {
            g.imagem = g.imagem?.includes('pps.whatsapp.net') ? undefined : g.imagem;
        });
        return grupos;

    }

    async marcarGrupoComoLido(idGrupo: IdGrupo) {
        return await this.f.patch(`/${idGrupo}/leitura`) as ResponseGrupo
    }
    async marcarGrupoComoNaoLido(idGrupo: IdGrupo) {
        return await this.f.patch(`/${idGrupo}/nao-lida`) as ResponseGrupo
    }

    async deletarGrupo(idGrupo: IdGrupo) {
        return await this.f.delete(`/${idGrupo}`) as void;
    }

    /**
     * Comunidade do grupo, com os subgrupos dela que este usuário pode ver nesta
     * inbox. A lista respeita a mesma regra de grupos restritos da listagem, então
     * pode ser menor do que a comunidade realmente tem.
     */
    async getComunidade(idGrupo: IdGrupo) {
        return await this.f.get(`/${idGrupo}/comunidade`) as ComunidadeDetalhe;
    }

    async getParticipantes(idGrupo: IdGrupo) {
        return await this.f.get(`/${idGrupo}/participantes`) as GrupoParticipanteResponseDto[];
    }
    
    async pinGrupo(idGrupo: IdGrupo): Promise<void> {
        await this.f.post(`/${idGrupo}/pin`, {});
    }

    async unpinGrupo(idGrupo: IdGrupo): Promise<void> {
        await this.f.delete(`/${idGrupo}/pin`);
    }

    async silenciarGrupo(idGrupo: IdGrupo, duracao: DuracaoSilencio) {
        return await this.f.post(`/${idGrupo}/silenciar`, { duracao }) as { silenciadoAte: IsoTimestamp|null };
    }

    async reativarNotificacoesDoGrupo(idGrupo: IdGrupo): Promise<void> {
        await this.f.delete(`/${idGrupo}/silenciar`);
    }

    async setWhitelistStatus(idGrupo: IdGrupo, whitelistStatus: boolean): Promise<void> {
        await this.f.patch(`/${idGrupo}/whitelist?whitelist-enabled=${whitelistStatus}`);
    }
}

export type GrupoParticipanteResponseDto = {
    codigo: string;
    /**
     * OMITIDO pelo servidor quando o identificador é LID e nada o resolveu — ele deixou
     * de repetir o identificador aqui de propósito. Opcional para o TypeScript poder
     * avisar quem consome.
     */
    nome?: string;
    admin: boolean;
    superAdmin: boolean;
    /** O identificador é um LID, não telefone. Fato do servidor; a leitura de "anônimo" é daqui. */
    ehLid?: boolean;
    idContato?: IdContato | null;
    imgUrl?: string | null;
}